/**
 * RecipeDrawer — the right-side panel that shows a recipe in view or edit
 * mode. Built for the redesign in PR-15 Phase B.
 *
 * Controller wiring example (drop this inside `Library.tsx` once Phase A
 * lands and `selectedRecipeId` is lifted into Library state):
 *
 * ```tsx
 * const selectedRecipe = useMemo(
 *   () => recipes.find((r) => r.recipeId === selectedRecipeId) ?? null,
 *   [recipes, selectedRecipeId]
 * );
 *
 * <RecipeDrawer
 *   recipe={selectedRecipe}
 *   racks={racks}
 *   userId={userId}
 *   recipes={recipes}
 *   setRecipes={setRecipes}
 *   onClose={() => setSelectedRecipeId(null)}
 * />
 * ```
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconShare2,
  IconPencil,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';

import {
  Drawer,
  Button,
  IconButton,
  Field,
  Loader,
  RackChip,
  useToast,
} from '../../design/components';
import {
  callScrapeAndSummarise,
  createShareLink,
  updateRecipe,
} from '@recipe-rack/shared';
import type { Rack, Recipe } from '@recipe-rack/shared';

import { db, functions } from './Firebase/InitialiseFirebase';
import RackPicker from './RackPicker';
import DeleteRecipeModal from './DeleteRecipeModal';

export interface RecipeDrawerProps {
  recipe: Recipe | null;
  racks: Rack[];
  userId: string;
  recipes: Recipe[];
  setRecipes: React.Dispatch<React.SetStateAction<Recipe[]>>;
  onClose: () => void;
}

/** Try to extract a display-friendly host from a URL — graceful on bad input. */
const sourceDomain = (link: string): string => {
  if (!link) return '';
  try {
    return new URL(link).host.replace(/^www\./, '');
  } catch {
    return link;
  }
};

const RecipeDrawer: React.FC<RecipeDrawerProps> = ({
  recipe,
  racks,
  userId,
  recipes,
  setRecipes,
  onClose,
}) => {
  const toast = useToast();

  // Local edit state — initialised from recipe and reset on close/cancel.
  const [isEditing, setIsEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftIngredients, setDraftIngredients] = useState('');
  const [draftMethod, setDraftMethod] = useState('');
  const [draftRackIds, setDraftRackIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sharing, setSharing] = useState(false);

  // When the selected recipe changes, reset draft + edit state.
  useEffect(() => {
    if (!recipe) return;
    setIsEditing(false);
    setDraftTitle(recipe.title);
    setDraftIngredients((recipe.ingredients ?? []).join('\n'));
    setDraftMethod((recipe.method ?? []).join('\n'));
    setDraftRackIds(recipe.rackIds ?? []);
  }, [recipe?.recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const recipeRackChips = useMemo(() => {
    if (!recipe) return [];
    const ids = recipe.rackIds ?? [];
    return ids
      .map((id) => racks.find((r) => r.rackId === id))
      .filter((r): r is Rack => r !== undefined);
  }, [recipe, racks]);

  if (!recipe) return null;

  const isPending = recipe.version === 0;
  const domain = sourceDomain(recipe.link);

  const handleSave = async () => {
    setSaving(true);
    const ingredients = draftIngredients
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const method = draftMethod
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const title = draftTitle.trim() || recipe.title;
    const fields = { title, ingredients, method, rackIds: draftRackIds };
    try {
      await updateRecipe(db, userId, recipe.recipeId, fields);
      // Optimistic local update so the drawer + library reflect immediately.
      setRecipes((current) =>
        current.map((r) =>
          r.recipeId === recipe.recipeId ? { ...r, ...fields } : r
        )
      );
      toast.show('Recipe updated', 'success');
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update recipe', err);
      toast.show('Could not save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraftTitle(recipe.title);
    setDraftIngredients((recipe.ingredients ?? []).join('\n'));
    setDraftMethod((recipe.method ?? []).join('\n'));
    setDraftRackIds(recipe.rackIds ?? []);
    setIsEditing(false);
  };

  const handleShare = async () => {
    if (!recipe || sharing) return;
    setSharing(true);
    try {
      // If the recipe already has a share token, reuse it — share links are
      // permanent. Otherwise mint a new one (also persists `shareToken` back
      // on the recipe doc).
      const token = recipe.shareToken ?? (await createShareLink(db, userId, recipe));
      const url = `${window.location.origin}/r/${token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.show('Share link copied to clipboard');
      } catch {
        // Clipboard can fail (older browsers, http context). Still useful to
        // tell the user the link exists.
        toast.show(url);
      }
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : 'Could not create share link',
        'error'
      );
    } finally {
      setSharing(false);
    }
  };

  const handleRefresh = async () => {
    if (!recipe || refreshing) return;
    setRefreshing(true);
    toast.show('Refreshing from source…');
    try {
      await callScrapeAndSummarise(functions, {
        link: recipe.link,
        recipeId: recipe.recipeId,
        userId,
        force_refresh: true,
      });
      toast.show('Refreshed from source');
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : 'Could not refresh from source',
        'error'
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <Drawer
        open={recipe !== null}
        onClose={onClose}
        position="right"
        title={recipe.title}
      >
        {isPending ? (
          <div className="rr-recipe-pending">
            <Loader
              block
              label="Racking up your recipe — usually under a minute."
            />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<IconTrash size={16} />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </div>
        ) : isEditing ? (
          <div className="rr-recipe-edit-form">
            <Field
              label="Title"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              className="rr-recipe-title-field"
            />
            <Field
              label="Ingredients"
              description="One per line."
              multiline
              rows={6}
              value={draftIngredients}
              onChange={(e) => setDraftIngredients(e.target.value)}
            />
            <Field
              label="Method"
              description="One step per line."
              multiline
              rows={8}
              value={draftMethod}
              onChange={(e) => setDraftMethod(e.target.value)}
            />
            <div className="rr-field-wrap">
              <div className="rr-field-label">Racks</div>
              <RackPicker
                racks={racks}
                selected={draftRackIds}
                onChange={setDraftRackIds}
              />
            </div>
            <div className="rr-recipe-edit-actions">
              <Button variant="ghost" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {(domain || recipe.imageUrl) && (
              <div className="rr-recipe-meta">
                {domain && (
                  recipe.link ? (
                    <a
                      href={recipe.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {domain}
                    </a>
                  ) : (
                    <span>{domain}</span>
                  )
                )}
              </div>
            )}

            {recipe.imageUrl && (
              <img
                className="rr-recipe-image"
                src={recipe.imageUrl}
                alt=""
                aria-hidden="true"
              />
            )}

            {recipeRackChips.length > 0 && (
              <div className="rr-recipe-racks">
                {recipeRackChips.map((rack) => (
                  <RackChip key={rack.rackId} static>
                    {rack.name}
                  </RackChip>
                ))}
              </div>
            )}

            <section className="rr-recipe-section">
              <h3 className="rr-recipe-eyebrow">Ingredients</h3>
              {recipe.ingredients && recipe.ingredients.length > 0 ? (
                <ul className="rr-recipe-ingredients">
                  {recipe.ingredients.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: 'var(--ink-mute)', fontSize: 13, margin: 0 }}>
                  No ingredients yet.
                </p>
              )}
            </section>

            <section className="rr-recipe-section">
              <h3 className="rr-recipe-eyebrow">Method</h3>
              {recipe.method && recipe.method.length > 0 ? (
                <ol className="rr-recipe-method">
                  {recipe.method.map((step, i) => (
                    <li key={i}>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p style={{ color: 'var(--ink-mute)', fontSize: 13, margin: 0 }}>
                  No method yet.
                </p>
              )}
            </section>

            <div className="rr-recipe-actions">
              <Link
                to={`/cook/${recipe.recipeId}`}
                className="rr-btn rr-btn-tomato rr-recipe-cook"
              >
                Cook this →
              </Link>
              <Button
                variant="ghost"
                leftIcon={<IconShare2 size={16} />}
                onClick={handleShare}
                loading={sharing}
              >
                Share
              </Button>
              <Button
                variant="ghost"
                leftIcon={<IconPencil size={16} />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
              <IconButton
                icon={<IconRefresh size={18} />}
                aria-label="Re-scrape from source"
                onClick={handleRefresh}
                disabled={refreshing}
              />
              <IconButton
                icon={<IconTrash size={18} />}
                aria-label="Delete recipe"
                variant="danger"
                onClick={() => setDeleteOpen(true)}
                style={{ gridColumn: '1 / -1', justifySelf: 'end' }}
              />
            </div>
          </>
        )}
      </Drawer>

      <DeleteRecipeModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={onClose}
        userId={userId}
        recipe={recipe}
      />
    </>
  );
};

export default RecipeDrawer;
